import { makeAutoObservable } from "mobx";
import { initTypeIcons } from "../../Obj2Tab/utils/typeIcons";
import { CatalogStore } from "../model/catalog";
import { ReferencesStore } from "../model/references";
import { ResourceState } from "../model/resourceState";
import { SelectionStore } from "../model/selection";
import { NodeLoading } from "./loading";
import { NodeSelection } from "./selection";

/**
 * Открытие вкладки.
 *
 * Загружаются справочники и каталог объектов. Детали ни одного объекта
 * при первом входе не запрашиваются: это и есть та ленивость, ради которой
 * всё затевалось.
 *
 * Возврат на вкладку — другое: каталог перечитывается, и уже раскрытые
 * объекты тоже, иначе второй пользователь не увидит чужие правки, пока
 * не обновит страницу. Дерево при этом не гасится: поверх него крутится
 * индикатор, как у LoaderBox, когда данные уже были. Нераскрытые по-прежнему
 * грузятся только по клику.
 *
 * Ещё одно исключение - возврат с ранее выбранным узлом: без его данных
 * форма осталась бы пустой. Это одна загрузка и только по факту прежнего
 * выбора.
 *
 * Здесь же ловится смена пользователя: выход и вход не перезагружают страницу,
 * поэтому иначе вкладка показала бы прежнее дерево с прежними правами.
 */
export class Startup {
  state: ResourceState<true> = { status: "none" };

  /** Повторный вход: дерево на месте, поверх него индикатор. */
  refreshing = false;

  private sessionKey: string | null = null;

  constructor(
    private readonly references: ReferencesStore,
    private readonly catalog: CatalogStore,
    private readonly selection: SelectionStore,
    private readonly nodeSelection: NodeSelection,
    private readonly loading: NodeLoading,
    private readonly currentSessionKey: () => string,
    private readonly reset: () => void,
  ) {
    makeAutoObservable<
      Startup,
      | "catalog"
      | "currentSessionKey"
      | "loading"
      | "nodeSelection"
      | "references"
      | "reset"
      | "selection"
    >(this, {
      references: false,
      catalog: false,
      selection: false,
      nodeSelection: false,
      loading: false,
      currentSessionKey: false,
      reset: false,
    });
  }

  private setSessionKey(key: string) {
    this.sessionKey = key;
  }

  private setState(next: ResourceState<true>) {
    this.state = next;
  }

  private setRefreshing(on: boolean) {
    this.refreshing = on;
  }

  async run() {
    const session = this.currentSessionKey();
    if (this.sessionKey !== null && this.sessionKey !== session) {
      this.reset();
      this.setState({ status: "none" });
    }
    this.setSessionKey(session);
    // Первый вход — полноэкранный спиннер. Повторный — оверлей, дерево
    // остаётся: иначе Ant Tree сбросит раскрытие, а пользователь увидит пустоту.
    const overlay = this.state.status === "ready";
    if (overlay) this.setRefreshing(true);
    else this.setState({ status: "wait" });
    try {
      await this.references.load(initTypeIcons);
      await this.catalog.load();
      await this.loading.refreshAfterCatalogReload();
      this.setState({ status: "ready", value: true });
    } catch (error) {
      this.setState({ status: "error", error });
      return;
    } finally {
      this.setRefreshing(false);
    }
    const selected = this.selection.selectedId;
    if (selected) await this.nodeSelection.select(selected);
  }
}
